import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Avatar,
  Alert,
  CircularProgress,
  Checkbox,
  Tooltip,
  Switch,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Person,
  Email,
  Phone,
  CalendarToday,
  PersonAdd,
  PersonRemove,
  PowerSettingsNew,
  MoreVert,
} from '@mui/icons-material';
import { Pagination } from '@mui/material';
import { Patient } from '../types';
import { patientService } from '../services/patientService';

const Patients: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [genderFilter, setGenderFilter] = useState<'all' | 'male' | 'female' | 'other'>('all');
  const [selectedPatients, setSelectedPatients] = useState<number[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null);

  // Fetch patients from API
  const fetchPatients = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {
        page,
        limit,
        search,
      };
      
      if (statusFilter === 'active') {
        params.isActive = true;
      } else if (statusFilter === 'inactive') {
        params.isActive = false;
      }

      if (genderFilter !== 'all') {
        params.gender = genderFilter;
      }

      const response = await patientService.getPatients(params);
      setPatients(response.data.patients);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.totalItems);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch patients.');
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter, genderFilter]);

  // Reset page to 1 when search or filters change
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, genderFilter]);

  // Fetch patients when component mounts or dependencies change
  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const [open, setOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    gender: 'male' as 'male' | 'female' | 'other',
    address: '',
    emergencyContact: '',
    medicalHistory: '',
    allergies: '',
    currentMedications: '',
    password: '',
  });

  const handleOpen = (patient?: Patient) => {
    if (patient) {
      setEditingPatient(patient);
      setFormData({
        fullName: patient.fullName,
        email: patient.email,
        phoneNumber: patient.phoneNumber,
        dateOfBirth: patient.dateOfBirth || '',
        gender: patient.gender || 'male',
        address: patient.address || '',
        emergencyContact: patient.emergencyContact || '',
        medicalHistory: patient.medicalHistory || '',
        allergies: patient.allergies || '',
        currentMedications: patient.currentMedications || '',
        password: '',
      });
    } else {
      setEditingPatient(null);
      setFormData({
        fullName: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        gender: 'male',
        address: '',
        emergencyContact: '',
        medicalHistory: '',
        allergies: '',
        currentMedications: '',
        password: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingPatient(null);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      const patientData = {
        fullName: formData.fullName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        address: formData.address,
        emergencyContact: formData.emergencyContact,
        medicalHistory: formData.medicalHistory,
        allergies: formData.allergies,
        currentMedications: formData.currentMedications,
        ...(editingPatient ? {} : { password: formData.password || 'defaultPassword123' }),
      };

      if (editingPatient) {
        await patientService.updatePatient(editingPatient.id, patientData);
        setSuccessMessage('Patient updated successfully!');
      } else {
        await patientService.createPatient(patientData);
        setSuccessMessage('Patient created successfully!');
      }

      await fetchPatients();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save patient.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this patient?')) {
      try {
        setLoading(true);
        setError('');
        await patientService.deletePatient(id);
        setSuccessMessage('Patient deleted successfully!');
        await fetchPatients();
      } catch (err: any) {
        setError(err.message || 'Failed to delete patient.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatusToggle = async (id: number, isActive: boolean) => {
    try {
      setLoading(true);
      setError('');
      await patientService.updatePatientStatus(id, isActive);
      setSuccessMessage(`Patient ${isActive ? 'activated' : 'deactivated'} successfully!`);
      await fetchPatients();
    } catch (err: any) {
      setError(err.message || 'Failed to update patient status.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle page change
  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Handle menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, patientId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatientId(patientId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPatientId(null);
  };

  // Handle patient selection
  const handleSelectPatient = (patientId: number) => {
    setSelectedPatients(prev => 
      prev.includes(patientId) 
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  // Handle select all patients
  const handleSelectAllPatients = () => {
    setSelectedPatients(
      selectedPatients.length === patients.length 
        ? [] 
        : patients.map(p => p.id)
    );
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async (isActive: boolean) => {
    if (selectedPatients.length === 0) return;
    
    try {
      setLoading(true);
      setError('');
      await patientService.bulkUpdatePatients(selectedPatients, { isActive });
      setSuccessMessage(`${selectedPatients.length} patients ${isActive ? 'activated' : 'deactivated'} successfully!`);
      setSelectedPatients([]);
      await fetchPatients();
    } catch (err: any) {
      setError(err.message || 'Failed to update patients status.');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Patients</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen()}
          disabled={loading}
        >
          Add Patient
        </Button>
      </Box>

      {/* Success and Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6">{patients.length}</Typography>
                  <Typography color="textSecondary">Total Patients</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {patients.filter(p => p.isActive).length}
                  </Typography>
                  <Typography color="textSecondary">Active Patients</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {patients.filter(p => !p.isActive).length}
                  </Typography>
                  <Typography color="textSecondary">Inactive Patients</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <CalendarToday />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {patients.filter(p => p.gender === 'male').length}
                  </Typography>
                  <Typography color="textSecondary">Male Patients</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search patients"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or phone"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Gender</InputLabel>
              <Select
                value={genderFilter}
                label="Gender"
                onChange={(e) => setGenderFilter(e.target.value as any)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {selectedPatients.length > 0 && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  {selectedPatients.length} patient(s) selected
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleBulkStatusUpdate(true)}
                  disabled={loading}
                  startIcon={<PersonAdd />}
                  sx={{
                    backgroundColor: '#00A76F',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#007867',
                    },
                    '&:disabled': {
                      backgroundColor: '#E0E0E0',
                      color: '#9E9E9E',
                    }
                  }}
                >
                  Activate All
                </Button>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleBulkStatusUpdate(false)}
                  disabled={loading}
                  startIcon={<PersonRemove />}
                  sx={{
                    backgroundColor: '#FF5630',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#E53E3E',
                    },
                    '&:disabled': {
                      backgroundColor: '#E0E0E0',
                      color: '#9E9E9E',
                    }
                  }}
                >
                  Deactivate All
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Patients Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedPatients.length === patients.length && patients.length > 0}
                    indeterminate={selectedPatients.length > 0 && selectedPatients.length < patients.length}
                    onChange={handleSelectAllPatients}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patients.map((patient) => (
                <TableRow key={patient.id} selected={selectedPatients.includes(patient.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedPatients.includes(patient.id)}
                      onChange={() => handleSelectPatient(patient.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {patient.fullName.charAt(0)}
                      </Avatar>
                      {patient.fullName}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Email sx={{ mr: 1, fontSize: 16 }} />
                      {patient.email}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Phone sx={{ mr: 1, fontSize: 16 }} />
                      {patient.phoneNumber}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {patient.dateOfBirth ? calculateAge(patient.dateOfBirth) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={patient.gender || 'N/A'}
                      color={patient.gender === 'male' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Switch
                        checked={patient.isActive}
                        onChange={(e) => handleStatusToggle(patient.id, e.target.checked)}
                        color="success"
                        size="small"
                      />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: patient.isActive ? '#00A76F' : '#FF5630',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        {patient.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Tooltip title="Edit Patient">
                        <IconButton
                          size="small"
                          onClick={() => handleOpen(patient)}
                          sx={{ 
                            color: '#1976D2',
                            '&:hover': {
                              backgroundColor: '#E3F2FD',
                              color: '#0D47A1'
                            }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="More Actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, patient.id)}
                          sx={{ 
                            color: '#666',
                            '&:hover': {
                              backgroundColor: '#F5F5F5',
                              color: '#333'
                            }
                          }}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {/* {totalPages > 1 && ( */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalItems)} of {totalItems} patients
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      {/* )} */}
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPatient ? 'Edit Patient' : 'Add New Patient'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={formData.gender}
                  label="Gender"
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingPatient ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 180,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
          }
        }}
      >
        {selectedPatientId && patients.find(p => p.id === selectedPatientId) && (
          <>
            <MenuItem
              onClick={() => {
                const patient = patients.find(p => p.id === selectedPatientId);
                if (patient) {
                  handleOpen(patient);
                }
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Patient</ListItemText>
            </MenuItem>
            
            <MenuItem
              onClick={() => {
                const patient = patients.find(p => p.id === selectedPatientId);
                if (patient) {
                  handleStatusToggle(patient.id, !patient.isActive);
                }
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <PowerSettingsNew fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {patients.find(p => p.id === selectedPatientId)?.isActive ? 'Deactivate' : 'Activate'}
              </ListItemText>
            </MenuItem>
            
            <MenuItem
              onClick={() => {
                handleDelete(selectedPatientId);
                handleMenuClose();
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <Delete fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText>Delete Patient</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default Patients;
